// layers/base/app/i18n/locales.ts
//
// Single source of truth for which locales the product supports and which are
// "ready" (have translations). The Nuxt i18n config, the language switcher,
// and locale normalization all derive from this list. English is the default
// and the universal fallback; non-English files start empty and fall back to
// English until translated (see docs/i18n/translation-workflow.md).

export interface AppLocale {
  /** Locale code used by @nuxtjs/i18n and the `i18n_locale` cookie. */
  code: string
  /** Native display name shown in the language switcher. */
  name: string
  /** BCP-47 tag for the `lang`/`hreflang` attribute. */
  bcp47: string
  /** Text direction; all current locales are LTR. */
  dir: 'ltr' | 'rtl'
  /** Gate-by-ready: only `true` locales appear in the switcher. */
  ready: boolean
  /** ISO 3166-1 alpha-2 country code for the switcher flag (language ≠ country). */
  flag: string
}

export const LOCALES: AppLocale[] = [
  // Order = display order in the language switcher (readyLocales() preserves it).
  // English first (primary + universal fallback); the rest by approximate
  // market size; Ukrainian last. Only `ready: true` locales are shown.
  { code: 'en', name: 'English', bcp47: 'en', dir: 'ltr', ready: true, flag: 'gb' },
  { code: 'es', name: 'Español', bcp47: 'es', dir: 'ltr', ready: true, flag: 'es' },
  { code: 'pt-BR', name: 'Português (BR)', bcp47: 'pt-BR', dir: 'ltr', ready: true, flag: 'br' },
  { code: 'ru', name: 'Русский', bcp47: 'ru', dir: 'ltr', ready: true, flag: 'ru' },
  { code: 'tr', name: 'Türkçe', bcp47: 'tr', dir: 'ltr', ready: true, flag: 'tr' },
  { code: 'de', name: 'Deutsch', bcp47: 'de', dir: 'ltr', ready: true, flag: 'de' },
  { code: 'zh', name: '中文', bcp47: 'zh-Hans', dir: 'ltr', ready: true, flag: 'cn' },
  { code: 'uk', name: 'Українська', bcp47: 'uk', dir: 'ltr', ready: true, flag: 'ua' },
]

export const DEFAULT_LOCALE = 'en'

export const SUPPORTED_CODES: string[] = LOCALES.map((l) => l.code)

export const READY_CODES: string[] = LOCALES.filter((l) => l.ready).map((l) => l.code)

export function getLocale(code: string): AppLocale | undefined {
  return LOCALES.find((l) => l.code === code)
}

export function readyLocales(): AppLocale[] {
  return LOCALES.filter((l) => l.ready)
}
